import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 系統頁的「管理權限」提示（T07／#14 收尾，之後在 #518 收斂成「只在真的 401／403 才出現」）。
 *
 * 舊版只看兩件事：`isAdminAuthRequired()` 為真 ＋ sessionStorage 沒有 token → 立刻說
 * 「系統頁需要管理權限」。它不知道真正的原因，於是後端 500、斷網、token 放在別的儲存空間
 * 過期，全都長成同一句權限提示，使用者以為自己帳號不能看就不重試了。
 *
 * 現在顯示條件完全交給共用分類（`api/admin-access.ts` 的 `resolveAdminAccess()`，
 * 它自己再借用 `api/data-state.ts` 的 `errorKindOf()`）：
 *   ① 有 token 且請求成功／還沒打過 → **不渲染**
 *   ② 有 token 但回 401／403        → 「管理憑證已失效」＋重新登入（token 已清掉）
 *   ③ 完全沒有 token                → 「系統頁需要管理權限」＋可收合的一次性設定
 *   ④ 有 token 但 5xx／斷網         → **不渲染**（權限提示會騙人，交給頁面自己的錯誤態）
 *
 * 存好之後 `onSaved` 讓外殼重掛目前分頁（各頁自己重新 fetch），不整頁 reload。
 * 這支不碰 storage：讀寫一律走 `api/client.ts` 的 `readAdminToken`／`writeAdminToken`。
 * 外殼對總覽／工作階段／AI 傳 `needsAdminToken={false}`，這支不渲染。
 * `X-Admin-Token` 仍是機器可選路徑，不靠這塊提示才能打 `/api/admin/*`。
 */
import { useEffect, useState } from 'react';
import { isAdminAuthRequired, onAdminUnauthorized, readAdminToken, writeAdminToken, } from '../../api/client';
import { initialAdminAccess, resolveAdminAccess, shouldShowAdminAccess, } from '../../api/admin-access';
export function AdminAccessNotice({ onSaved, onRelogin, needsAdminToken = true, staffSession = false, } = {}) {
    /** 旗標在 `createHttpApiClient()` 就設好；mock 站永遠不顯示。 */
    const required = isAdminAuthRequired();
    /**
     * 管理端點且完全沒有憑證，才先說要 Admin Token。
     * 控制面頁（`needsAdminToken` 為 false）靠 session，不在這裡停住。
     * 有 token 就先當作沒事，真的被 401／403 再來（`onAdminUnauthorized`）。
     */
    const [access, setAccess] = useState(() => required && needsAdminToken ? initialAdminAccess(readAdminToken() !== '') : { kind: 'none' });
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState('');
    useEffect(() => {
        if (!required || !needsAdminToken)
            return;
        return onAdminUnauthorized((message) => {
            // 一定要**先**讀（`adminRequest` 沒有 token 時本來就是空的；有 token 被拒時還在）。
            const hadToken = readAdminToken() !== '';
            if (hadToken) {
                // 憑證過期／撤銷：留著只會讓下一個請求再撞一次 401，直接清掉。
                writeAdminToken('');
                setValue('');
            }
            // 這條通道只送 401／403（`http-client` 的 `adminRequest`），所以字樣認不出來時仍算權限。
            setAccess(resolveAdminAccess({ error: message, hasToken: hadToken, fromAuthChannel: true }));
        });
    }, [required, needsAdminToken]);
    if (!required || !needsAdminToken || (!shouldShowAdminAccess(access) && !open))
        return null;
    const kind = access.kind === 'expired' ? 'expired' : 'missing';
    const staffMissing = staffSession && kind === 'missing';
    const submit = (event) => {
        event.preventDefault();
        const next = value.trim();
        if (next === '')
            return;
        writeAdminToken(next);
        setValue('');
        // 存好就先當作沒事：真的有問題，下一個請求的 401 會再把它叫回來。
        setAccess({ kind: 'none' });
        setOpen(false);
        onSaved?.();
    };
    return (_jsxs("section", { className: "cui-panel flex flex-col gap-1.5 p-3", role: "status", "aria-label": "\u7BA1\u7406\u6B0A\u9650", "data-testid": "admin-access-notice", "data-state": kind, children: [_jsx("h2", { className: "cui-panel-title", "data-testid": "admin-access-title", children: kind === 'expired' ? '管理憑證已失效' : staffMissing ? '這一頁要 Admin Token' : '系統頁需要管理權限' }), _jsx("p", { className: "text-[12px] leading-snug cui-muted", "data-testid": "admin-access-body", children: kind === 'expired'
                    ? '這台機器記著的管理憑證被後端拒絕了（過期、被撤銷，或不是這台後端簽發的）。請重新登入；重新登入後會帶上新的權限。'
                    : staffMissing
                        ? '登入角色（engineer 或 admin）跟 X-Admin-Token 是兩把不同的鑰匙。監控與運行參數用目前的登入就能看。總覽、工作階段與 AI 成效還要在下面貼 Admin Token，只存在這個分頁。重新登入不會補上這把鑰匙。'
                        : '你目前登入的身分沒有管理權限，所以這裡的資料讀不到。請換一個有管理權限的帳號重新登入；若你已經是管理員，重新登入一次就會帶上新權限。' }), 'message' in access && access.message ? (_jsx("p", { className: "text-[12px] leading-snug", style: { color: 'var(--ak-color-danger)' }, "data-testid": "admin-access-error", children: access.message })) : null, _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [onRelogin === undefined || staffMissing ? null : (_jsx("button", { type: "button", className: "cui-btn cui-btn--sm cui-btn--primary", onClick: onRelogin, "data-testid": "admin-access-relogin", children: "\u91CD\u65B0\u767B\u5165" })), _jsx("button", { type: "button", className: "cui-btn cui-btn--sm cui-btn--ghost", onClick: () => setOpen((prev) => !prev), "aria-expanded": open, "aria-controls": "admin-token-settings", "data-testid": "admin-access-setup-toggle", children: open ? '取消這次設定' : staffMissing ? '設定 Admin Token' : '一次性設定（僅這台機器）' })] }), open ? (_jsxs("form", { id: "admin-token-settings", className: "flex flex-wrap items-center gap-2", onSubmit: submit, "aria-label": "\u4E00\u6B21\u6027\u6B0A\u9650\u8A2D\u5B9A", children: [_jsx("input", { id: "admin-access-token", type: "password", className: "cui-input", style: { flex: '1 1 220px', maxWidth: 320 }, value: value, onChange: (event) => setValue(event.target.value), placeholder: "\u8CBC\u4E0A\u7BA1\u7406\u6B0A\u9650\u78BC", "aria-label": "\u7BA1\u7406\u6B0A\u9650\u78BC", autoComplete: "off", spellCheck: false, autoFocus: true, "data-testid": "admin-access-input" }), _jsx("button", { type: "submit", className: "cui-btn cui-btn--sm cui-btn--primary", "data-testid": "admin-access-save", children: "\u5132\u5B58" })] })) : null] }));
}
//# sourceMappingURL=AdminAccessNotice.js.map